import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, ShieldCheck } from "lucide-react";

// ---------------------------------------------------------------------------
// MP12 Module 4 §D — APPROVED / SEND confirmation dialog.
//
// Every named condition must be explicitly ticked. The backend re-validates
// pathway + compliance server-side. If outbound transport isn't configured,
// the snapshot is HELD; the record NEVER claims delivery.
// ---------------------------------------------------------------------------

const CONFIRMATIONS = [
  { key: "confirm_selected_assets_correct",                       label: "Selected assets are correct." },
  { key: "confirm_each_asset_eligible_for_intended_use",          label: "Each selected asset is eligible for the intended use." },
  { key: "confirm_asset_pathway_permits_action",                  label: "Asset Pathway permits this action." },
  { key: "confirm_rights_and_compliance_sufficient",              label: "Rights and compliance status are sufficient for the specific opportunity." },
  { key: "confirm_recipient_details_correct",                     label: "Recipient details are correct." },
  { key: "confirm_deadline_correct",                              label: "Deadline is correct." },
  { key: "confirm_delivery_method_complies_with_classification",  label: "Delivery method complies with classification / security rules." },
  { key: "confirm_no_restricted_master_stem_wav_via_ordinary_email", label: "No restricted file or unrestricted master / stem / WAV delivery is being sent through ordinary external email." },
];

export function ApproveSendDialog({ open, onOpenChange, brief, onApproved }) {
  const [confs, setConfs] = useState(Object.fromEntries(CONFIRMATIONS.map((c) => [c.key, false])));
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientDisplayName, setRecipientDisplayName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [classification, setClassification] = useState("RESTRICTED");
  const [busy, setBusy] = useState(false);

  const allTicked = CONFIRMATIONS.every((c) => !!confs[c.key]);

  function reset() {
    setConfs(Object.fromEntries(CONFIRMATIONS.map((c) => [c.key, false])));
    setRecipientEmail(""); setRecipientDisplayName("");
    setSubject(""); setBody(""); setClassification("RESTRICTED");
  }

  async function submit() {
    if (!brief?.id) return;
    if (!allTicked) {
      toast.error("Every confirmation must be ticked before APPROVED / SEND.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/sync-briefs/${brief.id}/approve-send`, {
        ...confs,
        recipient_email: recipientEmail.trim(),
        recipient_display_name: recipientDisplayName.trim(),
        cover_message_subject: subject.trim(),
        cover_message_body: body.trim(),
        communication_classification: classification.trim().toUpperCase() || "RESTRICTED",
      });
      const state = r.data?.delivery_state || "";
      if (state === "SENT") {
        toast.success("APPROVED / SENT — snapshot created and outbound package released.");
      } else {
        toast.warning(`APPROVED. Snapshot HELD (${state}). No delivery has occurred.`);
      }
      onApproved && onApproved(r.data);
      reset();
      onOpenChange && onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "APPROVED / SEND refused by the server.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { onOpenChange && onOpenChange(v); if (!v) reset(); } }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="scd-approve-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" style={{ color: "hsl(347 60% 22%)" }} />
            APPROVED / SEND
          </DialogTitle>
          <DialogDescription className="text-[12.5px]">
            Consequential action. Freezes a versioned submission snapshot and creates a linked Communications Hub record. If outbound transport is not configured, the package is HELD; delivery is never fabricated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10.5px] uppercase tracking-wide">Recipient email</Label>
              <Input value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} className="mt-1 h-8 text-[12px]" placeholder="e.g. supervisor@example.com" data-testid="scd-approve-recipient" />
            </div>
            <div>
              <Label className="text-[10.5px] uppercase tracking-wide">Recipient display name</Label>
              <Input value={recipientDisplayName} onChange={(e) => setRecipientDisplayName(e.target.value)} className="mt-1 h-8 text-[12px]" data-testid="scd-approve-recipient-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10.5px] uppercase tracking-wide">Cover subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 h-8 text-[12px]" data-testid="scd-approve-subject" />
            </div>
            <div>
              <Label className="text-[10.5px] uppercase tracking-wide">Classification</Label>
              <Input value={classification} onChange={(e) => setClassification(e.target.value)} className="mt-1 h-8 text-[12px]" placeholder="RESTRICTED" data-testid="scd-approve-classification" />
            </div>
          </div>
          <div>
            <Label className="text-[10.5px] uppercase tracking-wide">Cover body (draft — never auto-sent)</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} className="mt-1 text-[12px]" data-testid="scd-approve-body" />
          </div>

          <div className="space-y-1.5 border rounded p-3" style={{ background: "hsl(40 55% 96%)", borderColor: "hsl(40 55% 78%)" }}>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Required confirmations</div>
            {CONFIRMATIONS.map((c) => (
              <label key={c.key} className="flex items-start gap-2 text-[12px] cursor-pointer" data-testid={`scd-approve-conf-${c.key}`}>
                <Checkbox checked={!!confs[c.key]} onCheckedChange={(v) => setConfs((prev) => ({ ...prev, [c.key]: !!v }))} className="mt-0.5" />
                <span>{c.label}</span>
              </label>
            ))}
          </div>

          <div className="rounded border p-2.5 flex items-start gap-2 text-[11.5px] text-muted-foreground" style={{ background: "hsl(0 55% 96%)", borderColor: "hsl(0 55% 82%)" }}>
            <AlertTriangle className="w-4 h-4 mt-0.5" style={{ color: "hsl(0 65% 32%)" }} />
            <p>
              APPROVED / SEND freezes the current asset list as a versioned snapshot and locks the brief. This action is audited. Verified records are never modified retroactively by this action.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange && onOpenChange(false)} disabled={busy} data-testid="scd-approve-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !allTicked} style={{ background: "hsl(347 72% 22%)" }} data-testid="scd-approve-submit">
            {busy ? "Working…" : "APPROVED / SEND"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ApproveSendDialog;
